import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef, ElementRef, Injector,
  Input, OnChanges,
  OnInit, Renderer2, SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {LazyLoadedComponentDefinition} from './lazy-loaded-component.definition';
import {DynamicComponent} from './dynamic.component';

@Component({
  selector: 'app-lazy-loaded-component',
  templateUrl: './lazy-loaded-component.component.html',
  styleUrls: ['./lazy-loaded-component.component.scss']
})
export class LazyLoadedComponentComponent implements OnInit, OnChanges {

  @Input() definition: LazyLoadedComponentDefinition;
  @ViewChild('vc', { read: ViewContainerRef }) vc: ViewContainerRef | undefined;
  loaded: boolean;

  componentRef: ComponentRef<any>;
  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private injector: Injector,
              private appRef: ApplicationRef,) { }


  async ngOnInit(): Promise<void> {
    this.loaded = false;
    const {module, component} = await this.loadComponent();
    this.loaded = true;
    this.componentRef = await this.render(component);
    const simpleChanges = this.bindInputs();
    this.bindOutputs();
    this.triggerDetectChanges(simpleChanges);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.definition) {
      const simpleChanges = this.bindInputs();
      this.triggerDetectChanges(simpleChanges);
    }
  }

  async loadComponent() {
    const module = await this.definition.module();
    const ctor = module.prototype.constructor;
    const instance = new ctor();
    const onLoadMethod = instance.onModuleLoad;
    if (onLoadMethod) {
      await onLoadMethod(this.componentFactoryResolver, this.injector, this.appRef);
    }
    const component = await this.definition.component();
    return {module, component};
  }

  async render(component: Type<DynamicComponent>) {
    if (!this.vc) {
      return;
    }
    this.vc.clear();
    const factory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = this.vc.createComponent(factory);
    return componentRef;
  }

  private bindInputs(): SimpleChanges {
    if (!this.componentRef) {
      return;
    }
    const inputs = this.definition.inputs;
    if (!inputs) {
      return;
    }
    const oldInputs = {};
    const newInputs = {};
    Object.keys(inputs).forEach(key => {
      oldInputs[key] = this.componentRef.instance[key];
      this.componentRef.instance[key] = inputs[key];
      newInputs[key] = inputs[key];
    });
    const changes: SimpleChanges = {};
    Object.keys(oldInputs).forEach(key => {
      changes[key] = {
        previousValue: oldInputs[key],
        currentValue: newInputs[key],
        firstChange: false,
        isFirstChange: () => false
      };
    });
    return changes;
  }

  private bindOutputs() {
    if (!this.componentRef) {
      return;
    }
    const outputs = this.definition.outputs;
    if (!outputs) {
      return;
    }
    Object.keys(outputs).forEach(key => {
      const eventEmitter = this.componentRef.instance[key];
      if (!eventEmitter) {
        return;
      }
      if (outputs[key]) {
        eventEmitter.subscribe(outputs[key].bind(this.definition.instance));
      }
    });
  }

  private triggerDetectChanges(changes: SimpleChanges) {
    if (!this.componentRef) {
      return;
    }
    const detectChanges = this.componentRef.instance.detectChanges;
    if (detectChanges) {
      detectChanges();
    }
    let ngOnChanges = this.componentRef.instance.ngOnChanges;
    if (ngOnChanges) {
      ngOnChanges = ngOnChanges.bind(this.componentRef.instance);
      ngOnChanges(changes);
    }
  }
}

